import React, { createContext, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { createTheme, ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";

/* ==========================================================================
   CRM LASIT — TEMA OSCURO PERMANENTE v3.0
   PROHIBIDO MODIFICAR SIN AUTORIZACIÓN  |  Trae AI  |  2026-05-19
   ========================================================================== */

const ThemeContext = createContext();

// Paleta oficial CRM LASIT
const LASIT = {
  bg:       "#060A14",
  paper:    "#0D1B2A",
  paperAlt: "#0F2233",
  border:   "rgba(0,212,170,0.15)",
  accent:   "#00D4AA",
  text:     "#FFFFFF",
  textMuted:"rgba(255,255,255,0.55)",
};

export const ThemeProvider = ({ children }) => {
  const [darkMode] = useState(true);
  const toggleTheme = () => {};

  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          button: { textTransform: "none", fontWeight: 600 },
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Ubuntu, sans-serif",
          allVariants: { color: LASIT.text },
        },
        palette: {
          type: "dark",
          primary:   { main: LASIT.accent, light: "#00E8BB", dark: "#00B89A", contrastText: "#060A14" },
          secondary: { main: "#0099CC" },
          error:     { main: "#FF6B6B" },
          warning:   { main: "#FFB347" },
          success:   { main: "#00D4AA" },
          background: { default: LASIT.bg, paper: LASIT.paper },
          text: { primary: LASIT.text, secondary: LASIT.textMuted },
          divider: LASIT.border,
          action:  { hover: "rgba(0,212,170,0.07)", selected: "rgba(0,212,170,0.12)" },
        },
        shape: { borderRadius: 10 },

        overrides: {
          // ── 1. CSS GLOBAL ─────────────────────────────────────────────────────
          MuiCssBaseline: {
            "@global": {
              body: {
                backgroundColor: `${LASIT.bg} !important`,
                color: `${LASIT.text} !important`,
                lineHeight: 1.6,
              },
              "*::-webkit-scrollbar":       { width: 6, height: 6 },
              "*::-webkit-scrollbar-track": { background: "transparent" },
              "*::-webkit-scrollbar-thumb": { background: "rgba(0,212,170,0.3)", borderRadius: 3 },
              "*::-webkit-scrollbar-thumb:hover": { background: "rgba(0,212,170,0.6)" },

              // ── Fondos blancos por inline style ─────────────────────────────
              "[style*='background-color: white'] *, [style*='background-color: #fff'] *, [style*='background-color: rgb(255, 255, 255)'] *":
                { color: "#1a1a2e !important" },
              "[style*='background-color: #f5f5f5'] *, [style*='background-color: rgb(245, 245, 245)'] *":
                { color: "#1a1a2e !important" },
              "[style*='background-color: #f1f8e9'] *, [style*='background-color: rgb(241, 248, 233)'] *":
                { color: "#1a1a2e !important" },

              // ── Textarea (sin !important para permitir overrides locales) ──────
              "textarea": {
                backgroundColor: `${LASIT.paper}`,
                color: LASIT.text,
                caretColor: LASIT.accent,
                border: `1px solid ${LASIT.border}`,
                borderRadius: 8,
                padding: "12px",
              },
              
              // Evitamos forzar TODOS los inputs a blanco para no dañar librerías externas (como react-color)
              // Los inputs de Material-UI ya están cubiertos por .MuiInputBase-input
              "input[type='text']:not(.MuiInputBase-input):not([id^='rc-editable-input'])": {
                color: `${LASIT.text}`,
              },

              // ── Corrección específica para inputs de react-color (TwitterPicker, etc) ──
              "[id^='rc-editable-input']": {
                color: "#1a1a2e !important",
                backgroundColor: "#ffffff !important",
              },

              // ── Tabs / Base de Conocimiento ──────────────────────────────────
              ".MuiTab-root":                  { color: `${LASIT.textMuted} !important` },
              ".MuiTab-textColorPrimary.Mui-selected": { color: `${LASIT.accent} !important` },
              ".MuiTabs-indicator":            { backgroundColor: `${LASIT.accent} !important` },

              // ── Sombras ───────────────────────────────────────────────────────
              ".MuiPaper-elevation1": { boxShadow: "0 4px 20px rgba(0,0,0,0.4) !important" },
              ".MuiPaper-elevation2": { boxShadow: "0 8px 32px rgba(0,0,0,0.45) !important" },

              // ── Tooltip ───────────────────────────────────────────────────────
              ".MuiTooltip-tooltip": {
                backgroundColor: "#1A2B3C !important",
                color: `${LASIT.text} !important`,
                border: `1px solid ${LASIT.border}`,
                borderRadius: "6px !important",
                fontSize: "0.78rem !important",
                fontWeight: 600,
                padding: "6px 10px !important",
              },
              ".MuiTooltip-arrow": { color: "#1A2B3C !important" },

              // ── Chips ─────────────────────────────────────────────────────────
              ".MuiChip-root": {
                backgroundColor: "rgba(0,212,170,0.1) !important",
                color: `${LASIT.accent} !important`,
                border: `1px solid rgba(0,212,170,0.25) !important`,
              },

              // ── Menús y Popovers ──────────────────────────────────────────────
              ".MuiMenu-paper, .MuiPopover-paper": {
                backgroundColor: `${LASIT.paperAlt} !important`,
                border: `1px solid ${LASIT.border}`,
                boxShadow: "0 8px 32px rgba(0,0,0,0.5) !important",
              },
              ".MuiMenuItem-root":       { color: `${LASIT.text} !important` },
              ".MuiMenuItem-root:hover": { backgroundColor: "rgba(0,212,170,0.08) !important" },

              // ── Dialogs ───────────────────────────────────────────────────────
              ".MuiDialog-paper": {
                backgroundColor: `${LASIT.paper} !important`,
                border: `1px solid ${LASIT.border}`,
                boxShadow: "0 24px 64px rgba(0,0,0,0.7) !important",
              },
              ".MuiDialogTitle-root": {
                backgroundColor: `${LASIT.paper} !important`,
                color: `${LASIT.text} !important`,
                borderBottom: `1px solid ${LASIT.border}`,
              },
              ".MuiDialogContent-root": { backgroundColor: `${LASIT.paper} !important` },
              ".MuiDialogActions-root": {
                backgroundColor: `${LASIT.paper} !important`,
                borderTop: `1px solid ${LASIT.border}`,
              },

              // ── Inputs en MUI ─────────────────────────────────────────────────
              ".MuiInputBase-input, .MuiOutlinedInput-input": { color: `${LASIT.text} !important` },
              "select": { backgroundColor: `${LASIT.paper} !important`, color: `${LASIT.text} !important` },

              // ── Tablas ────────────────────────────────────────────────────────
              ".MuiTableRow-root:hover td": { backgroundColor: "rgba(0,212,170,0.04) !important" },
              ".MuiTableRow-root td":       { color: `${LASIT.text} !important` },

              // ── Campos personalizados / Folder chips ──────────────────────────
              "[class*='folderPaper'], [class*='folderName']": { color: "#1a1a2e !important" },

              // ── Twitter color picker ──────────────────────────────────────────
              ".twitter-picker": {
                backgroundColor: `${LASIT.paper} !important`,
                boxShadow: "0 8px 32px rgba(0,0,0,0.5) !important",
              },
              "input#rc-editable-input-1, input[id^='rc-editable-input']": {
                color: "#000000 !important",
              },
            },
          },

          // ── 2. PAPER ────────────────────────────────────────────────────────────
          MuiPaper: {
            root: { backgroundImage: "none", backgroundColor: LASIT.paper, color: LASIT.text },
            rounded: { borderRadius: 12, border: `1px solid ${LASIT.border}` },
            elevation0: { boxShadow: "none" },
            elevation1: { boxShadow: "0 4px 20px rgba(0,0,0,0.35)" },
            elevation2: { boxShadow: "0 8px 32px rgba(0,0,0,0.45)" },
            elevation3: { boxShadow: "0 12px 40px rgba(0,0,0,0.5)" },
          },

          // ── 3. BUTTON ────────────────────────────────────────────────────────────
          MuiButton: {
            root: { borderRadius: 8, padding: "8px 16px", transition: "all 0.2s ease-in-out", fontWeight: 600 },
            contained: { boxShadow: "none", "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.3)", transform: "translateY(-1px)" } },
            containedPrimary: { backgroundColor: LASIT.accent, color: "#060A14", "&:hover": { backgroundColor: "#00E8BB" } },
            outlined: { borderColor: LASIT.border },
            outlinedPrimary: { borderColor: LASIT.accent, color: LASIT.accent, "&:hover": { backgroundColor: "rgba(0,212,170,0.08)" } },
            text: { color: LASIT.text },
          },

          // ── 4. APPBAR ────────────────────────────────────────────────────────────
          MuiAppBar: {
            root: { boxShadow: "none" },
            colorPrimary: { backgroundColor: LASIT.bg, color: LASIT.text },
          },

          // ── 5. DRAWER ────────────────────────────────────────────────────────────
          MuiDrawer: {
            paper: { backgroundColor: LASIT.bg, borderRight: `1px solid ${LASIT.border}` },
          },

          // ── 6. LIST ──────────────────────────────────────────────────────────────
          MuiListItem: {
            root: {
              borderRadius: 8,
              "&$selected": { backgroundColor: "rgba(0,212,170,0.15)", color: LASIT.accent,
                              "&:hover": { backgroundColor: "rgba(0,212,170,0.22)" } },
            },
            button: { "&:hover": { backgroundColor: "rgba(0,212,170,0.07)" } },
          },
          MuiListItemIcon: { root: { color: "inherit", minWidth: 40 } },

          // ── 7. INPUTS ────────────────────────────────────────────────────────────
          MuiOutlinedInput: {
            root: {
              borderRadius: 8,
              backgroundColor: "rgba(255,255,255,0.03)",
              "& $notchedOutline": { borderColor: LASIT.border },
              "&:hover $notchedOutline": { borderColor: "rgba(0,212,170,0.4)" },
              "&$focused $notchedOutline": { borderColor: LASIT.accent, borderWidth: 2 },
            },
            input: { color: LASIT.text },
          },
          MuiInputBase: { root: { color: LASIT.text }, input: { color: `${LASIT.text} !important` } },
          MuiInputLabel: {
            root: {
              color: LASIT.textMuted,
              "&$focused": {
                color: `${LASIT.accent} !important`,
              },
            },
          },
          MuiSelect:     { root: { color: LASIT.text }, icon: { color: LASIT.textMuted } },

          // ── 8. TABLE ─────────────────────────────────────────────────────────────
          MuiTableCell: {
            root: { borderBottom: `1px solid ${LASIT.border}`, padding: 16, color: LASIT.text },
            head: { fontWeight: 700, color: LASIT.textMuted, backgroundColor: LASIT.bg,
                    textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.8px" },
            body: { color: LASIT.text },
          },
          MuiTableRow: {
            root: { "&:hover": { backgroundColor: "rgba(0,212,170,0.04) !important" } },
            head: { backgroundColor: `${LASIT.bg} !important` },
          },

          // ── 9. TABS ──────────────────────────────────────────────────────────────
          MuiTab: {
            root: { color: LASIT.textMuted, "&$selected": { color: LASIT.accent },
                    minWidth: "auto", padding: "10px 16px", fontWeight: 600 },
          },
          MuiTabs: { indicator: { backgroundColor: LASIT.accent, height: 2 } },

          // ── 10. CHIP ─────────────────────────────────────────────────────────────
          MuiChip: {
            root: { backgroundColor: "rgba(0,212,170,0.1)", color: LASIT.accent,
                    border: `1px solid rgba(0,212,170,0.25)`, fontWeight: 600 },
            label: { color: LASIT.accent },
          },

          // ── 11. TOOLTIP ──────────────────────────────────────────────────────────
          MuiTooltip: {
            tooltip: { backgroundColor: "#0F2233", color: LASIT.text,
                       border: `1px solid ${LASIT.border}`, borderRadius: 6,
                       fontSize: "0.78rem", fontWeight: 600, padding: "6px 10px" },
            arrow: { color: "#0F2233" },
          },

          // ── 12. DIALOG ───────────────────────────────────────────────────────────
          MuiDialog: { paper: { backgroundColor: LASIT.paper, border: `1px solid ${LASIT.border}`,
                                boxShadow: "0 24px 64px rgba(0,0,0,0.7)" } },
          MuiDialogTitle:   { root: { backgroundColor: LASIT.paper, color: LASIT.text,
                                      borderBottom: `1px solid ${LASIT.border}` } },
          MuiDialogContent: { root: { backgroundColor: LASIT.paper } },
          MuiDialogActions: { root: { backgroundColor: LASIT.paper, borderTop: `1px solid ${LASIT.border}` } },

          // ── 13. MENU / POPOVER ───────────────────────────────────────────────────
          MuiMenu:    { paper: { backgroundColor: LASIT.paperAlt } },
          MuiMenuItem:{ root: { color: LASIT.text, "&:hover": { backgroundColor: "rgba(0,212,170,0.08)" } } },
          MuiPopover: { paper: { backgroundColor: LASIT.paperAlt, border: `1px solid ${LASIT.border}`,
                                 boxShadow: "0 8px 32px rgba(0,0,0,0.5)" } },
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = { children: PropTypes.node.isRequired };

export const useThemeContext = () => useContext(ThemeContext);
